import {
  AlignLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  NotificationOutlined,
  PictureOutlined,
  DollarOutlined,
  DownOutlined,
  EnvironmentOutlined,
  FieldNumberOutlined,
  FontColorsOutlined,
  IdcardOutlined,
  LineOutlined,
  MailOutlined,
  OrderedListOutlined,
  PercentageOutlined,
  PhoneOutlined,
  ReadOutlined,
  SwapOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';

import type {
  WorkflowField,
  WorkflowFieldPropertiesPayload,
  WorkflowFieldType,
} from '@/api/workflows';

/**
 * Visual + textual metadata per WorkflowFieldType — the 4 BE primitives. Used by
 * the canvas chip and the right-panel header when a field is already placed.
 * The palette catalog is separate (see PALETTE_ENTRIES) because one BE type
 * may surface as multiple palette variants (e.g. Plain Text + Email + SIN all
 * persist as ShortText today; the BE type catalog grows later).
 */
type FieldTypeMeta = {
  type: WorkflowFieldType;
  labelKey: string;
  icon: () => ReactNode;
  tintBg: string;
  tintFg: string;
};

const FIELD_TYPE_META: FieldTypeMeta[] = [
  {
    type: 'ShortText',
    labelKey: 'workflow.builder.fields.ShortText.label',
    icon: () => <FontColorsOutlined />,
    tintBg: '#EFF6FF',
    tintFg: '#0179C1',
  },
  {
    type: 'LongText',
    labelKey: 'workflow.builder.fields.LongText.label',
    icon: () => <AlignLeftOutlined />,
    tintBg: '#ECFDF5',
    tintFg: '#059669',
  },
  {
    type: 'Number',
    labelKey: 'workflow.builder.fields.Number.label',
    icon: () => <FieldNumberOutlined />,
    tintBg: '#FEF2F2',
    tintFg: '#DC2626',
  },
  {
    type: 'SingleSelect',
    labelKey: 'workflow.builder.fields.SingleSelect.label',
    icon: () => <DownOutlined />,
    tintBg: '#F5F3FF',
    tintFg: '#7C3AED',
  },
  {
    type: 'RadioGroup',
    labelKey: 'workflow.builder.fields.RadioGroup.label',
    icon: () => <CheckCircleOutlined />,
    tintBg: '#FFF7ED',
    tintFg: '#C2410C',
  },
  {
    type: 'CheckboxGroup',
    labelKey: 'workflow.builder.fields.CheckboxGroup.label',
    icon: () => <CheckSquareOutlined />,
    // Same violet as the rest of the Selection category — the previous emerald
    // diverged from the palette card and felt off-tone on the canvas.
    tintBg: '#F5F3FF',
    tintFg: '#7C3AED',
  },
  {
    type: 'Toggle',
    labelKey: 'workflow.builder.fields.Toggle.label',
    icon: () => <SwapOutlined />,
    tintBg: '#F5F3FF',
    tintFg: '#7C3AED',
  },
  {
    type: 'Checkbox',
    labelKey: 'workflow.builder.fields.Checkbox.label',
    icon: () => <CheckOutlined />,
    tintBg: '#F5F3FF',
    tintFg: '#7C3AED',
  },
  {
    type: 'StaticText',
    labelKey: 'workflow.builder.fields.StaticText.label',
    icon: () => <FontColorsOutlined />,
    // Layout blocks use a neutral slate tint — they're not inputs, so they
    // shouldn't compete with text/numeric/selection colors.
    tintBg: '#F1F5F9',
    tintFg: '#475569',
  },
  {
    type: 'Paragraph',
    labelKey: 'workflow.builder.fields.Paragraph.label',
    icon: () => <ReadOutlined />,
    tintBg: '#F1F5F9',
    tintFg: '#475569',
  },
  {
    type: 'Divider',
    labelKey: 'workflow.builder.fields.Divider.label',
    icon: () => <LineOutlined />,
    tintBg: '#F1F5F9',
    tintFg: '#475569',
  },
  {
    type: 'Date',
    labelKey: 'workflow.builder.fields.Date.label',
    icon: () => <CalendarOutlined />,
    tintBg: '#EFF6FF',
    tintFg: '#0179C1',
  },
  {
    type: 'BulletedList',
    labelKey: 'workflow.builder.fields.BulletedList.label',
    icon: () => <UnorderedListOutlined />,
    tintBg: '#F1F5F9',
    tintFg: '#475569',
  },
  {
    type: 'Notice',
    labelKey: 'workflow.builder.fields.Notice.label',
    icon: () => <NotificationOutlined />,
    tintBg: '#F1F5F9',
    tintFg: '#475569',
  },
  {
    type: 'Image',
    labelKey: 'workflow.builder.fields.Image.label',
    icon: () => <PictureOutlined />,
    tintBg: '#F1F5F9',
    tintFg: '#475569',
  },
  {
    type: 'RepeatableRow',
    labelKey: 'workflow.builder.fields.RepeatableRow.label',
    icon: () => <OrderedListOutlined />,
    // Same indigo tint as the Compounds palette category — repeatable rows
    // sit conceptually next to compounds (both compose multiple sub-inputs).
    tintBg: '#ECFEFF',
    tintFg: '#0E7490',
  },
];

export const metaForFieldType = (type: WorkflowFieldType): FieldTypeMeta =>
  FIELD_TYPE_META.find((m) => m.type === type) ?? FIELD_TYPE_META[0]!;

/**
 * i18n key for the display label shown on the canvas chip + right-panel
 * header subtitle. Falls back to the field-type's static label, but for
 * ShortText / LongText with an InputRestriction set, returns a restriction-
 * specific key (e.g. "Digits Only Input"). Keeps the two surfaces in sync.
 */
export function fieldDisplayLabelKey(field: WorkflowField): string {
  if (field.type === 'ShortText' || field.type === 'LongText') {
    if (field.inputRestriction === 'digits') return 'workflow.builder.fields.ShortText.labelDigits';
    if (field.inputRestriction === 'letters')
      return 'workflow.builder.fields.ShortText.labelLetters';
    if (field.inputRestriction === 'alphanumeric')
      return 'workflow.builder.fields.ShortText.labelAlphanumeric';
  }
  return metaForFieldType(field.type).labelKey;
}

/**
 * A palette catalog entry, discriminated by <see cref="kind"/>:
 *
 *  - `field` — single-field entry shown in the left-rail palette. Click → arm
 *    → user clicks empty column → BE places one field via `placeField`.
 *
 *  - `compound` — multi-field blueprint (e.g. Canada-fixed Address: 2 rows,
 *    5 fields). Surfaced via each section's "+ Compound" dropdown (not the
 *    left palette). Click → one POST /sections/{secid}/compound that creates
 *    the whole structure atomically. The runtime form renderer can later
 *    overlay the compound with integrations (Canada Post autocomplete on
 *    Address, etc.); the design-time shape stays exactly what the blueprint
 *    produces.
 *
 * Both variants share the visual fields (label/description/icon/tints) so the
 * UI renders consistently regardless of kind.
 */
export type PaletteEntry = FieldPaletteEntry | CompoundPaletteEntry | CustomFieldPaletteEntry;

type PaletteVisual = {
  id: string;
  labelKey: string;
  descriptionKey: string;
  icon: () => ReactNode;
  tintBg: string;
  tintFg: string;
};

type FieldPaletteEntry = PaletteVisual & {
  kind: 'field';
  type: WorkflowFieldType;
  defaultLabelEn: string;
  defaultProperties: () => WorkflowFieldPropertiesPayload;
};

/**
 * A custom-element-library Field, armed/placed through the exact same arm-then-
 * click-empty-column flow as a built-in {@link FieldPaletteEntry} — only the
 * label/properties source differs (the saved library item instead of a static
 * catalog default). Built on the fly from a `CustomElement` by the Custom tab,
 * never part of the static `PALETTE_CATEGORIES` catalog.
 */
export type CustomFieldPaletteEntry = {
  kind: 'customField';
  id: string;
  type: WorkflowFieldType;
  defaultLabelEn: string;
  defaultLabelFr: string | null;
  // `required` is a top-level wire key, not part of the properties bag, so it
  // must be carried explicitly — otherwise a saved required field places as
  // optional (readOnly rides along inside defaultProperties and is unaffected).
  defaultRequired: boolean;
  defaultProperties: () => WorkflowFieldPropertiesPayload;
};

export type CompoundPaletteEntry = PaletteVisual & {
  kind: 'compound';
  blueprint: CompoundBlueprint;
  /** Default section title (EN + FR) the canvas uses when this compound is
   *  dropped from the palette — the originator can rename the section after. */
  defaultSectionTitleEn: string;
  defaultSectionTitleFr: string;
};

/**
 * Recipe the page hands to `workflowsApi.insertCompound`. Each row in the
 * blueprint maps to one new FormRow in the target section; each column-spec
 * becomes one WorkflowField placed in the matching column. The BE creates
 * the whole structure in a single MutateUnderLockAsync cycle.
 */
type CompoundBlueprint = {
  rows: CompoundRow[];
};

type CompoundRow = {
  // `null` slot = empty grid cell (the row reserves the width but no field is
  // placed). Lets a single narrow input sit on a wider grid track.
  columns: (CompoundField | null)[];
};

type CompoundField = {
  type: WorkflowFieldType;
  labelEn: string;
  /** Optional French label — falls through as null when omitted. */
  labelFr?: string;
  required: boolean;
  properties: WorkflowFieldPropertiesPayload;
};

type PaletteCategory = {
  id: string;
  labelKey: string;
  /** Static catalog entries only — `CustomFieldPaletteEntry` is built at render time from
   *  the originator's saved library items and never lives in this fixed catalog. */
  entries: (FieldPaletteEntry | CompoundPaletteEntry)[];
};

const TEXT_TINT = { bg: '#EFF6FF', fg: '#0179C1' };
const NUMERIC_TINT = { bg: '#FEF2F2', fg: '#DC2626' };
const SELECTION_TINT = { bg: '#F5F3FF', fg: '#7C3AED' };
const PRESET_TINT = { bg: '#FFF7ED', fg: '#C2410C' };
const COMPOUND_TINT = { bg: '#ECFEFF', fg: '#0E7490' };
// Layout blocks (StaticText / Paragraph / Divider) sit in their own slate
// family — they're not inputs and shouldn't compete with the input categories
// for visual attention.
const LAYOUT_TINT = { bg: '#F1F5F9', fg: '#475569' };

// Shared option list — referenced by the Address compound's Province dropdown.
// Two-letter codes are the canonical AcroForm values; EN/FR labels follow the
// federal-government spelling. Declared above PALETTE_CATEGORIES so the
// compound blueprint can reference it (const has no hoisting).
const CANADIAN_PROVINCES = [
  { value: 'AB', labelEn: 'Alberta', labelFr: 'Alberta' },
  { value: 'BC', labelEn: 'British Columbia', labelFr: 'Colombie-Britannique' },
  { value: 'MB', labelEn: 'Manitoba', labelFr: 'Manitoba' },
  { value: 'NB', labelEn: 'New Brunswick', labelFr: 'Nouveau-Brunswick' },
  { value: 'NL', labelEn: 'Newfoundland and Labrador', labelFr: 'Terre-Neuve-et-Labrador' },
  { value: 'NS', labelEn: 'Nova Scotia', labelFr: 'Nouvelle-Écosse' },
  { value: 'NT', labelEn: 'Northwest Territories', labelFr: 'Territoires du Nord-Ouest' },
  { value: 'NU', labelEn: 'Nunavut', labelFr: 'Nunavut' },
  { value: 'ON', labelEn: 'Ontario', labelFr: 'Ontario' },
  { value: 'PE', labelEn: 'Prince Edward Island', labelFr: 'Île-du-Prince-Édouard' },
  { value: 'QC', labelEn: 'Quebec', labelFr: 'Québec' },
  { value: 'SK', labelEn: 'Saskatchewan', labelFr: 'Saskatchewan' },
  { value: 'YT', labelEn: 'Yukon', labelFr: 'Yukon' },
];

/**
 * Address (Canada) compound — declared above PALETTE_CATEGORIES so the
 * Compounds category can reference it without hitting the const temporal
 * dead zone. Layout follows the BMO design reference: Apartment narrow on
 * a 2-col row, full-width Address, City + Province as a 2-up, Postal Code
 * narrow on a 2-col grid (col 2 reserved so the input doesn't stretch).
 * More compounds (Personal Info, Banking, Beneficiary, etc.) drop in as
 * sibling consts when authored.
 */
const ADDRESS_CANADA: CompoundPaletteEntry = {
  id: 'address-canada',
  kind: 'compound',
  labelKey: 'workflow.builder.palette.entries.addressCanada.label',
  descriptionKey: 'workflow.builder.palette.entries.addressCanada.description',
  icon: () => <EnvironmentOutlined />,
  tintBg: COMPOUND_TINT.bg,
  tintFg: COMPOUND_TINT.fg,
  defaultSectionTitleEn: 'Address',
  defaultSectionTitleFr: 'Adresse',
  blueprint: {
    rows: [
      {
        columns: [
          {
            type: 'ShortText',
            labelEn: 'Apartment',
            labelFr: 'Appartement',
            required: false,
            properties: { maxLength: 20, addressRole: 'apt' },
          },
          null,
        ],
      },
      {
        columns: [
          {
            type: 'ShortText',
            labelEn: 'Address',
            labelFr: 'Adresse',
            required: true,
            // Canada Post AddressComplete is pre-enabled on the compound's
            // street field so advisors get typeahead + auto-fill on the
            // sibling City / Province / Postal Code fields out of the box.
            // Authors can flip the checkbox off in the properties panel if
            // they want to disable it for a specific compound instance.
            // `addressRole: 'street'` makes the runtime treat this as the
            // autocomplete-driving slot; the other roles below are matched
            // section-locally on selection.
            properties: {
              maxLength: 200,
              addressAutocomplete: 'canada-post',
              addressRole: 'street',
            },
          },
        ],
      },
      {
        columns: [
          {
            type: 'ShortText',
            labelEn: 'City',
            labelFr: 'Ville',
            required: true,
            properties: { maxLength: 80, addressRole: 'city' },
          },
          {
            type: 'SingleSelect',
            labelEn: 'Province',
            labelFr: 'Province',
            required: true,
            properties: { options: CANADIAN_PROVINCES, addressRole: 'province' },
          },
        ],
      },
      {
        columns: [
          {
            type: 'ShortText',
            labelEn: 'Postal Code',
            labelFr: 'Code postal',
            required: true,
            properties: { maxLength: 7, placeholder: 'A1A 1A1', addressRole: 'postal' },
          },
          null,
        ],
      },
    ],
  },
};

export const PALETTE_CATEGORIES: PaletteCategory[] = [
  {
    id: 'text',
    labelKey: 'workflow.builder.palette.category.text',
    entries: [
      {
        id: 'plain-text',
        kind: 'field',
        type: 'ShortText',
        labelKey: 'workflow.builder.palette.entries.plainText.label',
        descriptionKey: 'workflow.builder.palette.entries.plainText.description',
        icon: () => <FontColorsOutlined />,
        tintBg: TEXT_TINT.bg,
        tintFg: TEXT_TINT.fg,
        defaultLabelEn: 'Text field',
        defaultProperties: () => ({ maxLength: 200 }),
      },
      {
        id: 'email',
        kind: 'field',
        type: 'ShortText',
        labelKey: 'workflow.builder.palette.entries.email.label',
        descriptionKey: 'workflow.builder.palette.entries.email.description',
        icon: () => <MailOutlined />,
        tintBg: TEXT_TINT.bg,
        tintFg: TEXT_TINT.fg,
        defaultLabelEn: 'Email',
        // `validate: 'email'` wires the on-blur format check the moment the
        // card lands on the canvas — no manual trip to the Validation
        // dropdown. Same pattern for phone/postal/SIN below.
        defaultProperties: () => ({
          maxLength: 200,
          placeholder: 'name@example.com',
          validate: 'email',
        }),
      },
      {
        id: 'phone',
        kind: 'field',
        type: 'ShortText',
        labelKey: 'workflow.builder.palette.entries.phone.label',
        descriptionKey: 'workflow.builder.palette.entries.phone.description',
        icon: () => <PhoneOutlined />,
        tintBg: TEXT_TINT.bg,
        tintFg: TEXT_TINT.fg,
        defaultLabelEn: 'Phone number',
        // Masked rule — `validate: 'phone'` enables on-blur 10-digit
        // check AND the live `(xxx) xxx-xxxx` keystroke mask. The mask owns
        // length so buildProperties nulls maxLength on the wire at save time.
        defaultProperties: () => ({
          maxLength: 20,
          placeholder: '(xxx) xxx-xxxx',
          validate: 'phone',
        }),
      },
      {
        id: 'postal-code',
        kind: 'field',
        type: 'ShortText',
        labelKey: 'workflow.builder.palette.entries.postalCode.label',
        descriptionKey: 'workflow.builder.palette.entries.postalCode.description',
        icon: () => <EnvironmentOutlined />,
        tintBg: TEXT_TINT.bg,
        tintFg: TEXT_TINT.fg,
        defaultLabelEn: 'Postal code',
        // Masked rule — Canadian A1A 1A1 alphabet + uppercase + space at
        // position 4. Mask owns length.
        defaultProperties: () => ({
          maxLength: 7,
          placeholder: 'A1A 1A1',
          validate: 'postal',
        }),
      },
      {
        id: 'sin',
        kind: 'field',
        type: 'ShortText',
        labelKey: 'workflow.builder.palette.entries.sin.label',
        descriptionKey: 'workflow.builder.palette.entries.sin.description',
        icon: () => <IdcardOutlined />,
        tintBg: TEXT_TINT.bg,
        tintFg: TEXT_TINT.fg,
        defaultLabelEn: 'Social insurance number',
        // Masked rule — `validate: 'sin'` enables Luhn checksum + first-digit
        // + the `XXX XXX XXX` space-separated mask. Placeholder updated to
        // match the actual mask output (spaces, not dashes).
        defaultProperties: () => ({
          maxLength: 11,
          placeholder: 'XXX XXX XXX',
          validate: 'sin',
        }),
      },
      {
        id: 'date',
        kind: 'field',
        type: 'Date',
        labelKey: 'workflow.builder.palette.entries.date.label',
        descriptionKey: 'workflow.builder.palette.entries.date.description',
        icon: () => <CalendarOutlined />,
        tintBg: TEXT_TINT.bg,
        tintFg: TEXT_TINT.fg,
        defaultLabelEn: 'Date',
        // Default hint is the locale-aware mask the picker shows on hover.
        defaultProperties: () => ({ hintEn: 'DD/MM/YYYY', hintFr: 'JJ/MM/AAAA' }),
      },
      {
        id: 'text-area',
        kind: 'field',
        type: 'LongText',
        labelKey: 'workflow.builder.palette.entries.textArea.label',
        descriptionKey: 'workflow.builder.palette.entries.textArea.description',
        icon: () => <AlignLeftOutlined />,
        tintBg: TEXT_TINT.bg,
        tintFg: TEXT_TINT.fg,
        defaultLabelEn: 'Comments',
        defaultProperties: () => ({ maxLength: 1000 }),
      },
    ],
  },
  {
    id: 'numeric',
    labelKey: 'workflow.builder.palette.category.numeric',
    entries: [
      {
        id: 'number',
        kind: 'field',
        type: 'Number',
        labelKey: 'workflow.builder.palette.entries.number.label',
        descriptionKey: 'workflow.builder.palette.entries.number.description',
        icon: () => <FieldNumberOutlined />,
        tintBg: NUMERIC_TINT.bg,
        tintFg: NUMERIC_TINT.fg,
        defaultLabelEn: 'Number',
        defaultProperties: () => ({}),
      },
      {
        id: 'currency',
        kind: 'field',
        type: 'Number',
        labelKey: 'workflow.builder.palette.entries.currency.label',
        descriptionKey: 'workflow.builder.palette.entries.currency.description',
        icon: () => <DollarOutlined />,
        tintBg: NUMERIC_TINT.bg,
        tintFg: NUMERIC_TINT.fg,
        defaultLabelEn: 'Amount',
        defaultProperties: () => ({ min: 0, step: 0.01, numberFormat: 'currency' }),
      },
      {
        id: 'percentage',
        kind: 'field',
        type: 'Number',
        labelKey: 'workflow.builder.palette.entries.percentage.label',
        descriptionKey: 'workflow.builder.palette.entries.percentage.description',
        icon: () => <PercentageOutlined />,
        tintBg: NUMERIC_TINT.bg,
        tintFg: NUMERIC_TINT.fg,
        defaultLabelEn: 'Percentage',
        defaultProperties: () => ({ min: 0, max: 100, step: 0.01, numberFormat: 'percentage' }),
      },
    ],
  },
  {
    id: 'selection',
    labelKey: 'workflow.builder.palette.category.selection',
    entries: [
      {
        id: 'dropdown',
        kind: 'field',
        type: 'SingleSelect',
        labelKey: 'workflow.builder.palette.entries.dropdown.label',
        descriptionKey: 'workflow.builder.palette.entries.dropdown.description',
        icon: () => <DownOutlined />,
        tintBg: SELECTION_TINT.bg,
        tintFg: SELECTION_TINT.fg,
        defaultLabelEn: 'Select an option',
        defaultProperties: () => ({
          options: [
            { value: 'option_1', labelEn: 'Option 1', labelFr: 'Option 1' },
            { value: 'option_2', labelEn: 'Option 2', labelFr: 'Option 2' },
          ],
        }),
      },
      {
        id: 'radio-group',
        kind: 'field',
        type: 'RadioGroup',
        labelKey: 'workflow.builder.palette.entries.radioGroup.label',
        descriptionKey: 'workflow.builder.palette.entries.radioGroup.description',
        icon: () => <UnorderedListOutlined />,
        tintBg: SELECTION_TINT.bg,
        tintFg: SELECTION_TINT.fg,
        defaultLabelEn: 'Choose one',
        // Seed with two placeholder options — the BE rejects empty options for
        // RadioGroup (same branch as SingleSelect). The originator renames them
        // via the right-rail OptionsEditor.
        defaultProperties: () => ({
          options: [
            { value: 'option_1', labelEn: 'Option 1', labelFr: 'Option 1' },
            { value: 'option_2', labelEn: 'Option 2', labelFr: 'Option 2' },
          ],
        }),
      },
      {
        id: 'checkbox-group',
        kind: 'field',
        type: 'CheckboxGroup',
        labelKey: 'workflow.builder.palette.entries.checkboxGroup.label',
        descriptionKey: 'workflow.builder.palette.entries.checkboxGroup.description',
        icon: () => <CheckSquareOutlined />,
        tintBg: SELECTION_TINT.bg,
        tintFg: SELECTION_TINT.fg,
        defaultLabelEn: 'Choose all that apply',
        defaultProperties: () => ({
          options: [
            { value: 'option_1', labelEn: 'Option 1', labelFr: 'Option 1' },
            { value: 'option_2', labelEn: 'Option 2', labelFr: 'Option 2' },
          ],
        }),
      },
      {
        id: 'toggle',
        kind: 'field',
        type: 'Toggle',
        labelKey: 'workflow.builder.palette.entries.toggle.label',
        descriptionKey: 'workflow.builder.palette.entries.toggle.description',
        icon: () => <SwapOutlined />,
        tintBg: SELECTION_TINT.bg,
        tintFg: SELECTION_TINT.fg,
        defaultLabelEn: 'Enable feature',
        defaultProperties: () => ({}),
      },
      {
        id: 'single-checkbox',
        kind: 'field',
        type: 'Checkbox',
        labelKey: 'workflow.builder.palette.entries.singleCheckbox.label',
        descriptionKey: 'workflow.builder.palette.entries.singleCheckbox.description',
        icon: () => <CheckOutlined />,
        tintBg: SELECTION_TINT.bg,
        tintFg: SELECTION_TINT.fg,
        defaultLabelEn: 'I agree to the terms',
        defaultProperties: () => ({}),
      },
    ],
  },
  {
    id: 'presets',
    labelKey: 'workflow.builder.palette.category.presets',
    entries: [
      {
        id: 'title-prefix',
        kind: 'field',
        type: 'RadioGroup',
        labelKey: 'workflow.builder.palette.entries.titlePrefix.label',
        descriptionKey: 'workflow.builder.palette.entries.titlePrefix.description',
        icon: () => <UserOutlined />,
        tintBg: PRESET_TINT.bg,
        tintFg: PRESET_TINT.fg,
        defaultLabelEn: 'Title',
        defaultProperties: () => ({
          options: [
            { value: 'mr', labelEn: 'Mr.', labelFr: 'M.' },
            { value: 'mrs', labelEn: 'Mrs.', labelFr: 'Mme' },
            { value: 'ms', labelEn: 'Ms.', labelFr: 'Mme' },
            { value: 'miss', labelEn: 'Miss', labelFr: 'Mlle' },
            { value: 'dr', labelEn: 'Dr.', labelFr: 'Dr' },
            { value: 'mx', labelEn: 'Mx.', labelFr: 'Mx' },
          ],
        }),
      },
      {
        id: 'gender',
        kind: 'field',
        type: 'RadioGroup',
        labelKey: 'workflow.builder.palette.entries.gender.label',
        descriptionKey: 'workflow.builder.palette.entries.gender.description',
        icon: () => <TeamOutlined />,
        tintBg: PRESET_TINT.bg,
        tintFg: PRESET_TINT.fg,
        defaultLabelEn: 'Gender',
        defaultProperties: () => ({
          options: [
            { value: 'male', labelEn: 'Male', labelFr: 'Homme' },
            { value: 'female', labelEn: 'Female', labelFr: 'Femme' },
            { value: 'other', labelEn: 'Other', labelFr: 'Autre' },
          ],
        }),
      },
      {
        id: 'yes-no',
        kind: 'field',
        type: 'RadioGroup',
        labelKey: 'workflow.builder.palette.entries.yesNo.label',
        descriptionKey: 'workflow.builder.palette.entries.yesNo.description',
        icon: () => <CheckCircleOutlined />,
        tintBg: PRESET_TINT.bg,
        tintFg: PRESET_TINT.fg,
        defaultLabelEn: 'Confirmation',
        defaultProperties: () => ({
          options: [
            { value: 'yes', labelEn: 'Yes', labelFr: 'Oui' },
            { value: 'no', labelEn: 'No', labelFr: 'Non' },
          ],
        }),
      },
    ],
  },
  {
    // Compounds — multi-field blueprints. Click a card to add a new section
    // pre-populated with the blueprint's rows + fields. Sits above Layout so
    // the input-shaped palette stays grouped above non-input layout blocks.
    id: 'compounds',
    labelKey: 'workflow.builder.palette.category.compounds',
    entries: [ADDRESS_CANADA],
  },
  {
    // Advanced — composite inputs that don't fit in a single column AND
    // aren't fixed-shape compounds. RepeatableRow lives here today; more
    // (e.g. file upload as a field, signature pad) join later.
    id: 'advanced',
    labelKey: 'workflow.builder.palette.category.advanced',
    entries: [
      {
        id: 'repeatable-row',
        kind: 'field',
        type: 'RepeatableRow',
        labelKey: 'workflow.builder.palette.entries.repeatableRow.label',
        descriptionKey: 'workflow.builder.palette.entries.repeatableRow.description',
        icon: () => <OrderedListOutlined />,
        tintBg: '#ECFEFF',
        tintFg: '#0E7490',
        defaultLabelEn: 'Repeatable Rows',
        // Default template: a single-column "Item" cell. The originator
        // edits the template via the properties panel after placement.
        defaultProperties: () => ({
          repeatableTemplate: [
            {
              id: crypto.randomUUID(),
              type: 'ShortText',
              labelEn: 'Item',
              required: true,
            },
          ],
        }),
      },
    ],
  },
  {
    id: 'layout',
    labelKey: 'workflow.builder.palette.category.layout',
    entries: [
      {
        id: 'static-text',
        kind: 'field',
        type: 'StaticText',
        labelKey: 'workflow.builder.palette.entries.staticText.label',
        descriptionKey: 'workflow.builder.palette.entries.staticText.description',
        icon: () => <FontColorsOutlined />,
        tintBg: LAYOUT_TINT.bg,
        tintFg: LAYOUT_TINT.fg,
        // Label IS the rendered content for layout blocks — seed with a sentence
        // the originator will overwrite, not with a placeholder identifier.
        defaultLabelEn: 'Please fill in all required fields.',
        defaultProperties: () => ({}),
      },
      {
        id: 'paragraph',
        kind: 'field',
        type: 'Paragraph',
        labelKey: 'workflow.builder.palette.entries.paragraph.label',
        descriptionKey: 'workflow.builder.palette.entries.paragraph.description',
        icon: () => <ReadOutlined />,
        tintBg: LAYOUT_TINT.bg,
        tintFg: LAYOUT_TINT.fg,
        defaultLabelEn:
          'The information you provide here is used to set up your account. Please review carefully before submitting.',
        defaultProperties: () => ({}),
      },
      {
        id: 'divider',
        kind: 'field',
        type: 'Divider',
        labelKey: 'workflow.builder.palette.entries.divider.label',
        descriptionKey: 'workflow.builder.palette.entries.divider.description',
        icon: () => <LineOutlined />,
        tintBg: LAYOUT_TINT.bg,
        tintFg: LAYOUT_TINT.fg,
        // Canvas-only identifier; never shown to the advisor.
        defaultLabelEn: 'Section break',
        defaultProperties: () => ({}),
      },
      {
        id: 'bulleted-list',
        kind: 'field',
        type: 'BulletedList',
        labelKey: 'workflow.builder.palette.entries.bulletedList.label',
        descriptionKey: 'workflow.builder.palette.entries.bulletedList.description',
        icon: () => <UnorderedListOutlined />,
        tintBg: LAYOUT_TINT.bg,
        tintFg: LAYOUT_TINT.fg,
        // Label is the canvas identifier, not user-facing — list items live in Options.
        defaultLabelEn: 'Bulleted list',
        defaultProperties: () => ({
          listStyle: 'disc',
          options: [
            { value: 'item_1', labelEn: 'First item', labelFr: 'Premier élément' },
            { value: 'item_2', labelEn: 'Second item', labelFr: 'Deuxième élément' },
          ],
        }),
      },
      {
        id: 'notice',
        kind: 'field',
        type: 'Notice',
        labelKey: 'workflow.builder.palette.entries.notice.label',
        descriptionKey: 'workflow.builder.palette.entries.notice.description',
        icon: () => <NotificationOutlined />,
        tintBg: LAYOUT_TINT.bg,
        tintFg: LAYOUT_TINT.fg,
        defaultLabelEn: 'Notice text goes here.',
        defaultProperties: () => ({
          icon: 'info',
          backgroundColor: '#EFF6FF',
        }),
      },
      {
        id: 'image',
        kind: 'field',
        type: 'Image',
        labelKey: 'workflow.builder.palette.entries.image.label',
        descriptionKey: 'workflow.builder.palette.entries.image.description',
        icon: () => <PictureOutlined />,
        tintBg: LAYOUT_TINT.bg,
        tintFg: LAYOUT_TINT.fg,
        // Label is the canvas identifier — the rendered content is the image
        // itself + the optional Caption below it. ImageUrl is intentionally
        // blank in defaults so the originator is forced to upload one (the
        // properties panel surfaces the upload affordance on first select).
        defaultLabelEn: 'Image',
        defaultProperties: () => ({
          imageWidthSize: 'full',
          imageAlignment: 'left',
        }),
      },
    ],
  },
];
